import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Stack,
    Chip,
    Checkbox,
    FormControlLabel,
    Tooltip,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useNavigate } from "react-router-dom";
import { authHeader, clearToken } from "../../auth";

// ⚠️ Point de départ du module "Timing Software" (chronométrage de
// qualifications — RS232/réseau, liste de départ, gestion des
// impulsions par canal, calculs de temps, pénalités, classement).
//
// Redesign du 27.08.2026 : statuts Athlètes/Départ en puces colorées,
// indicateur "Télémark" par event (timingEventMetaRouter.js) qui
// conditionne le lien "Pénalités", archivage/suppression.
//
// 29.08.2026 : plusieurs manches (1 à 3) et méthode de combinaison
// (addition / meilleure manche), dans timing_event_meta.
//
// 03.11.2026 : ces réglages (télémark, nombre de manches, méthode) se
// choisissent DÈS LA CRÉATION de la course et sont enregistrés tout de
// suite ; la liste ne sert plus qu'à les modifier si nécessaire. Les
// derniers choix sont repris comme valeurs par défaut pour la course
// suivante. Chaque enregistrement est vérifié (réponse du serveur) : en cas
// d'échec, un message s'affiche et les réglages sont relus, au lieu d'être
// perdus en silence.

const DEFAULTS_KEY = "timing-new-event-defaults";
function loadDefaults() {
    try {
        return { isTelemark: false, runCount: 1, combineMethod: "sum", ...JSON.parse(localStorage.getItem(DEFAULTS_KEY) || "{}") };
    } catch {
        return { isTelemark: false, runCount: 1, combineMethod: "sum" };
    }
}

const StatusDot = ({ ok, label }) => (
    <Tooltip title={`${label} : ${ok ? "oui" : "non"}`}>
        <Box
            sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: ok ? "#2e7d32" : "#bdbdbd",
                display: "inline-block",
            }}
        />
    </Tooltip>
);

export default function TimingSoftwareHub() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [meta, setMeta] = useState({}); // eventKey -> { isTelemark, runCount, combineMethod, archivedAt }
    const [newEventKey, setNewEventKey] = useState("");
    // Réglages de la nouvelle course (repris de la dernière création).
    const [newSettings, setNewSettings] = useState(loadDefaults);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [metaLoaded, setMetaLoaded] = useState(false);
    // Manche visée par les liens "Chrono" et "Liste de départ".
    const [navRun, setNavRun] = useState({});
    // Partage du lien de saisie des pénalités aux contrôleurs (WhatsApp).
    const [shareKey, setShareKey] = useState(null);
    const penaltyUrl = (k) => `${window.location.origin}/saisiepenalite/${encodeURIComponent(k)}`;
    const shareWhatsApp = (k) => {
        const text = `Saisie des pénalités — ${k}\n${penaltyUrl(k)}`;
        if (navigator.share) {
            navigator.share({ title: `Pénalités ${k}`, text, url: penaltyUrl(k) }).catch(() => {});
        } else {
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
        }
    };

    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            });
            if (res.status === 401) {
                clearToken();
                navigate("/login");
                return null;
            }
            return res;
        },
        [navigate]
    );

    const loadEvents = useCallback(() => {
        apiFetch("/api/timing/events")
            .then((res) => (res && res.ok ? res.json() : []))
            .then((data) => setEvents(data || []))
            .catch((err) => setError(err.message));
    }, [apiFetch]);

    const loadMeta = useCallback(() => {
        apiFetch("/api/timing/event-meta")
            .then((res) => (res && res.ok ? res.json() : []))
            .then((data) => {
                const map = {};
                (data || []).forEach((m) => {
                    map[m.eventKey] = m;
                });
                setMeta(map);
            })
            .catch((err) => setError(err.message))
            .finally(() => setMetaLoaded(true));
    }, [apiFetch]);

    useEffect(() => {
        loadEvents();
        loadMeta();
    }, [loadEvents, loadMeta]);

    // Enregistre une partie des réglages d'une course (mise à jour partielle
    // côté serveur, voir timingEventMetaRouter.js) et VÉRIFIE la réponse.
    const saveMeta = async (eventKey, patch) => {
        const res = await apiFetch(`/api/timing/event-meta/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify(patch),
        });
        if (!res) return false;
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `HTTP ${res.status}`);
        }
        return true;
    };

    // Création d'une course : les réglages (télémark, manches, méthode) sont
    // enregistrés AVANT d'ouvrir la page Course & exports (choix FIS /
    // Swiss-Ski / Populaire + Raceheader), qui propose ensuite l'import des
    // concurrents (?next=import).
    const createEvent = async (eventKey) => {
        const key = eventKey.trim();
        if (!key) return;
        if (events.some((ev) => ev.event_key === key) || meta[key]) {
            if (!window.confirm(`La course « ${key} » existe déjà. L'ouvrir quand même ? (ses réglages ne sont pas modifiés)`)) return;
            navigate(`/timingsoftware/swissski/${encodeURIComponent(key)}`);
            return;
        }
        setCreating(true);
        setError("");
        try {
            const patch = {
                isTelemark: !!newSettings.isTelemark,
                runCount: Number(newSettings.runCount) || 1,
                combineMethod: newSettings.combineMethod || "sum",
            };
            await saveMeta(key, patch);
            try {
                localStorage.setItem(DEFAULTS_KEY, JSON.stringify(patch));
            } catch {
                /* sans importance */
            }
            navigate(
                `/timingsoftware/swissski/${encodeURIComponent(key)}?next=import${patch.isTelemark ? "&sector=TM" : "&sector=AL"}`
            );
        } catch (err) {
            setError(`Réglages de la course non enregistrés : ${err.message}`);
        } finally {
            setCreating(false);
        }
    };

    const goToImport = (eventKey) => {
        if (!eventKey.trim()) return;
        navigate(`/timingsoftware/athletes-import/${encodeURIComponent(eventKey.trim())}`);
    };

    // Modification d'un réglage dans la liste : affichage immédiat, puis
    // enregistrement vérifié ; en cas d'échec, message + relecture.
    const updateMeta = async (eventKey, patch) => {
        setMeta((prev) => ({ ...prev, [eventKey]: { ...(prev[eventKey] || {}), ...patch } }));
        setSaved("");
        try {
            await saveMeta(eventKey, patch);
            setSaved(`${eventKey} : réglages enregistrés ✓`);
            setTimeout(() => setSaved(""), 2500);
        } catch (err) {
            setError(`${eventKey} : réglage non enregistré (${err.message})`);
            loadMeta();
        }
    };

    const archiveEvent = async (eventKey) => {
        try {
            await apiFetch(`/api/timing/event-meta/${encodeURIComponent(eventKey)}/archive`, { method: "POST" });
            loadMeta();
        } catch (err) {
            setError(err.message);
        }
    };

    const unarchiveEvent = async (eventKey) => {
        try {
            await apiFetch(`/api/timing/event-meta/${encodeURIComponent(eventKey)}/unarchive`, { method: "POST" });
            loadMeta();
        } catch (err) {
            setError(err.message);
        }
    };

    const deleteEvent = async (eventKey) => {
        if (
            !window.confirm(
                `Supprimer "${eventKey}" ?\n\nCeci efface uniquement les métadonnées et les pénalités télémark ` +
                `associées — PAS les athlètes/liste de départ/temps de chrono (gérés ailleurs). Irréversible pour ` +
                `ce qui est effacé ici.`
            )
        ) {
            return;
        }
        try {
            await apiFetch(`/api/timing/event-meta/${encodeURIComponent(eventKey)}`, { method: "DELETE" });
            loadMeta();
        } catch (err) {
            setError(err.message);
        }
    };

    const visibleEvents = useMemo(
        () => events.filter((ev) => showArchived || !meta[ev.event_key]?.archivedAt),
        [events, meta, showArchived]
    );
    const archivedCount = useMemo(() => events.filter((ev) => meta[ev.event_key]?.archivedAt).length, [events, meta]);

    // Résumé lisible des réglages d'une course.
    const summary = (m) =>
        [
            m.isTelemark ? "Télémark" : "Alpin",
            `${m.runCount || 1} manche${(m.runCount || 1) > 1 ? "s" : ""}`,
            (m.runCount || 1) > 1 ? (m.combineMethod === "best" ? "meilleure manche" : "addition") : null,
        ]
            .filter(Boolean)
            .join(" · ");

    return (
        <Box p={3} maxWidth={900}>
            <Typography variant="h4" mb={1}>
                Timing Software
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Chronométrage de qualifications, indépendant des courses parallèles.
            </Typography>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
            {saved && (
                <Typography color="success.main" sx={{ mb: 2 }}>
                    {saved}
                </Typography>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" mb={2}>
                    Nouvelle course
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    label="Identifiant (ex: quali-2026-01-23)"
                    value={newEventKey}
                    onChange={(e) => setNewEventKey(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createEvent(newEventKey)}
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={newSettings.isTelemark ? "TM" : "AL"}
                        onChange={(_, v) => v && setNewSettings((s) => ({ ...s, isTelemark: v === "TM" }))}
                    >
                        <ToggleButton value="AL">Alpin</ToggleButton>
                        <ToggleButton value="TM">Télémark</ToggleButton>
                    </ToggleButtonGroup>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Manches</InputLabel>
                        <Select
                            label="Manches"
                            value={newSettings.runCount}
                            onChange={(e) => setNewSettings((s) => ({ ...s, runCount: Number(e.target.value) }))}
                        >
                            <MenuItem value={1}>1 manche</MenuItem>
                            <MenuItem value={2}>2 manches</MenuItem>
                            <MenuItem value={3}>3 manches</MenuItem>
                        </Select>
                    </FormControl>
                    {newSettings.runCount > 1 && (
                        <FormControl size="small" sx={{ minWidth: 190 }}>
                            <InputLabel>Classement</InputLabel>
                            <Select
                                label="Classement"
                                value={newSettings.combineMethod}
                                onChange={(e) => setNewSettings((s) => ({ ...s, combineMethod: e.target.value }))}
                            >
                                <MenuItem value="sum">Addition des manches</MenuItem>
                                <MenuItem value="best">Meilleure manche</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    <Button
                        variant="contained"
                        disabled={!newEventKey.trim() || creating}
                        onClick={() => createEvent(newEventKey)}
                        sx={{ ml: "auto" }}
                    >
                        {creating ? "Création…" : "Créer la course"}
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Ces réglages sont enregistrés dès la création ; ils restent modifiables dans la liste ci-dessous.
                </Typography>
            </Paper>

            {events.length > 0 && metaLoaded && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h6">Courses existantes</Typography>
                        {archivedCount > 0 && (
                            <Button size="small" onClick={() => setShowArchived((s) => !s)}>
                                {showArchived ? "Masquer" : "Afficher"} les archivées ({archivedCount})
                            </Button>
                        )}
                    </Box>
                    <Stack spacing={2} divider={<Box sx={{ borderBottom: "1px solid #eee" }} />}>
                        {visibleEvents.map((ev) => {
                            const m = meta[ev.event_key] || {};
                            const isArchived = !!m.archivedAt;
                            return (
                                <Box key={ev.event_key} sx={{ opacity: isArchived ? 0.5 : 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
                                        <Typography sx={{ fontWeight: "bold" }}>{ev.event_key}</Typography>
                                        <StatusDot ok={ev.hasAthletes} label="Athlètes" />
                                        <StatusDot ok={ev.hasStartlist} label="Liste de départ" />
                                        <Typography variant="caption" color="text.secondary">
                                            {summary(m)}
                                        </Typography>
                                        {isArchived && <Chip label="Archivée" size="small" />}

                                        <FormControlLabel
                                            sx={{ ml: 1 }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={!!m.isTelemark}
                                                    onChange={(e) => updateMeta(ev.event_key, { isTelemark: e.target.checked })}
                                                />
                                            }
                                            label="Télémark"
                                        />

                                        <FormControl size="small" sx={{ minWidth: 100 }}>
                                            <InputLabel>Manches</InputLabel>
                                            <Select
                                                label="Manches"
                                                value={m.runCount || 1}
                                                onChange={(e) => updateMeta(ev.event_key, { runCount: Number(e.target.value) })}
                                            >
                                                <MenuItem value={1}>1 manche</MenuItem>
                                                <MenuItem value={2}>2 manches</MenuItem>
                                                <MenuItem value={3}>3 manches</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {(m.runCount || 1) > 1 && (
                                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                                <InputLabel>Méthode</InputLabel>
                                                <Select
                                                    label="Méthode"
                                                    value={m.combineMethod || "sum"}
                                                    onChange={(e) => updateMeta(ev.event_key, { combineMethod: e.target.value })}
                                                >
                                                    <MenuItem value="sum">Addition des manches</MenuItem>
                                                    <MenuItem value="best">Meilleure manche</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}

                                        {(m.runCount || 1) > 1 && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Aller à :
                                                </Typography>
                                                {Array.from({ length: m.runCount || 1 }, (_, i) => i + 1).map((rn) => (
                                                    <Chip
                                                        key={rn}
                                                        label={`M${rn}`}
                                                        size="small"
                                                        clickable
                                                        color={(navRun[ev.event_key] || 1) === rn ? "primary" : "default"}
                                                        variant={(navRun[ev.event_key] || 1) === rn ? "filled" : "outlined"}
                                                        onClick={() => setNavRun((prev) => ({ ...prev, [ev.event_key]: rn }))}
                                                    />
                                                ))}
                                            </Box>
                                        )}

                                        <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                                            {isArchived ? (
                                                <Tooltip title="Désarchiver">
                                                    <IconButton size="small" onClick={() => unarchiveEvent(ev.event_key)}>
                                                        <UnarchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Archiver">
                                                    <IconButton size="small" onClick={() => archiveEvent(ev.event_key)}>
                                                        <ArchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Supprimer (métadonnées + pénalités uniquement)">
                                                <IconButton size="small" color="error" onClick={() => deleteEvent(ev.event_key)}>
                                                    <DeleteForeverIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                        <Chip label="Import concurrents" size="small" clickable onClick={() => goToImport(ev.event_key)} />
                                        <Chip
                                            label="Voir participants"
                                            size="small"
                                            clickable
                                            onClick={() => navigate(`/timingsoftware/athletes/${encodeURIComponent(ev.event_key)}`)}
                                        />
                                        <Chip
                                            label="Liste de départ"
                                            size="small"
                                            clickable
                                            onClick={() =>
                                                navigate(
                                                    `/timingsoftware/startlist/${encodeURIComponent(ev.event_key)}?run=${navRun[ev.event_key] || 1}`
                                                )
                                            }
                                        />
                                        <Chip
                                            label="🏁 Chrono"
                                            size="small"
                                            clickable
                                            color="error"
                                            variant="outlined"
                                            onClick={() =>
                                                navigate(
                                                    `/timingsoftware/chrono/${encodeURIComponent(ev.event_key)}?run=${navRun[ev.event_key] || 1}`
                                                )
                                            }
                                        />
                                        <Chip
                                            label="📋 Course & exports"
                                            size="small"
                                            clickable
                                            color="secondary"
                                            variant="outlined"
                                            onClick={() => navigate(`/timingsoftware/swissski/${encodeURIComponent(ev.event_key)}`)}
                                        />
                                        <Chip
                                            label="📡 Serveur FIS"
                                            size="small"
                                            clickable
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => navigate(`/timingsoftware/fis/${encodeURIComponent(ev.event_key)}`)}
                                        />
                                        {m.isTelemark && (
                                            <Chip
                                                label="🎿 Pénalités"
                                                size="small"
                                                clickable
                                                color="warning"
                                                variant="outlined"
                                                onClick={() => navigate(`/saisiepenalite/${encodeURIComponent(ev.event_key)}`)}
                                            />
                                        )}
                                        {m.isTelemark && (
                                            <Chip
                                                label="📤 Lien contrôleurs"
                                                size="small"
                                                clickable
                                                color="warning"
                                                variant="outlined"
                                                onClick={() => setShareKey(ev.event_key)}
                                            />
                                        )}
                                        {m.isTelemark && (
                                            <Chip
                                                label="📊 Résultats télémark"
                                                size="small"
                                                clickable
                                                color="warning"
                                                variant="outlined"
                                                onClick={() =>
                                                    navigate(`/timingsoftware/resultats-telemark/${encodeURIComponent(ev.event_key)}`)
                                                }
                                            />
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Paper>
            )}
            <Dialog open={!!shareKey} onClose={() => setShareKey(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Lien de saisie des pénalités — {shareKey}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        À envoyer aux contrôleurs (style / saut). Chacun ouvre le lien sur son téléphone.
                    </Typography>
                    {shareKey && (
                        <TextField fullWidth size="small" value={penaltyUrl(shareKey)} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => navigator.clipboard?.writeText(penaltyUrl(shareKey)).then(() => setSaved("Lien copié ✓"))}>Copier</Button>
                    <Button variant="contained" color="success" onClick={() => shareWhatsApp(shareKey)}>
                        📱 Partager (WhatsApp)
                    </Button>
                    <Button onClick={() => setShareKey(null)}>Fermer</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}